"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { SIGN_TEST_CALCULATOR_STRINGS } from "@/components/tools/sign-test-calculator";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { t } from "@/lib/i18n/interpolate";
import {
  apaP,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import type { SignTestResult } from "@/lib/tool-kit/stats-tests-more/sign-test";

/** Results for the sign test calculator. */

type Strings = typeof SIGN_TEST_CALCULATOR_STRINGS;

const SIGN_TABLE_LIMIT = 40;

function signLabel(difference: number, strings: Strings): string {
  if (difference > 0) {
    return strings.plus;
  }
  return difference < 0 ? strings.minus : strings.zero;
}

function workingP(
  r: SignTestResult,
  strings: Strings,
  fmt: NumberFormatter
): string {
  if (r.alternative === "less") {
    return t(strings.workingLess, {
      k: fmt(r.nPositive),
      p: formatP(r.pLess, fmt),
    });
  }
  if (r.alternative === "greater") {
    return t(strings.workingGreater, {
      k: fmt(r.nPositive),
      p: formatP(r.pGreater, fmt),
    });
  }
  const low = Math.min(r.nPositive, r.nNegative);
  const tail = r.nPositive <= r.nNegative ? r.pLess : r.pGreater;
  return t(strings.workingTwo, {
    low: fmt(low),
    tail: formatP(tail, fmt),
    p: formatP(r.pTwoSided, fmt),
  });
}

export function SignTestResults({
  r,
  differences,
  first,
  second,
  alpha,
  strings,
  fmt,
}: {
  r: SignTestResult;
  differences: readonly number[];
  /** The first measurement, or the values in one-sample mode. */
  first: readonly number[];
  /** The second measurement; null in one-sample mode. */
  second: readonly number[] | null;
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const reject = r.p < alpha;
  const levelText = fmt(r.level, {
    style: "percent",
    maximumFractionDigits: 1,
  });
  const apa = richTemplate(strings.apaTemplate, {
    s: fmt(r.nPositive),
    n: fmt(r.n),
    p: apaP(r.p, fmt),
  });
  const paired = second !== null;
  const head = paired
    ? [
        strings.pair,
        strings.firstColumn,
        strings.secondColumn,
        strings.difference,
        strings.sign,
      ]
    : [strings.pair, strings.value, strings.difference, strings.sign];
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard label={strings.positive} value={fmt(r.nPositive)} />
        <StatCard label={strings.negative} value={fmt(r.nNegative)} />
        <StatCard label={strings.ties} value={fmt(r.nZero)} />
        <StatCard label={strings.nUsed} value={fmt(r.n)} />
        <StatCard emphasis label={strings.pValue} value={formatP(r.p, fmt)} />
        <StatCard label={strings.proportion} value={n(r.proportionPositive)} />
      </StatGrid>
      <Decision reject={reject}>
        {t(reject ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(r.p, fmt),
        })}
      </Decision>
      <h3 className={WIDGET_H3}>{strings.pValuesHeading}</h3>
      <ResultTable
        head={[strings.alternative, strings.p]}
        rows={[
          { key: "two", cells: [strings.twoSided, formatP(r.pTwoSided, fmt)] },
          { key: "less", cells: [strings.less, formatP(r.pLess, fmt)] },
          {
            key: "greater",
            cells: [strings.greater, formatP(r.pGreater, fmt)],
          },
        ]}
      />
      <h3 className={WIDGET_H3}>{strings.intervalsHeading}</h3>
      <div className="space-y-2 text-foreground text-sm">
        <p>
          {t(strings.proportionInterval, {
            share: n(r.proportionPositive),
            level: levelText,
            lo: n(r.proportionLow),
            hi: n(r.proportionHigh),
          })}
        </p>
        <p>{t(strings.medianLine, { median: n(r.median) })}</p>
        <p>
          {r.medianInterval
            ? t(strings.medianInterval, {
                level: levelText,
                lo: n(r.medianInterval.lower),
                hi: n(r.medianInterval.upper),
                coverage: n(r.medianInterval.coverage),
                k: fmt(r.medianInterval.k),
              })
            : t(strings.medianIntervalNone, {
                total: fmt(r.total),
                level: levelText,
              })}
        </p>
        <p className={HINT}>
          {t(strings.normalLine, {
            z: n(r.z),
            p: formatP(r.pNormal, fmt),
          })}
        </p>
      </div>
      <h3 className={WIDGET_H3}>{strings.signsHeading}</h3>
      <p className={HINT}>
        {differences.length > SIGN_TABLE_LIMIT
          ? strings.signsHidden
          : strings.signsHint}
      </p>
      {differences.length > SIGN_TABLE_LIMIT ? null : (
        <ResultTable
          head={head}
          rows={differences.map((d, i) => ({
            key: `pair-${i + 1}`,
            cells: paired
              ? [
                  fmt(i + 1),
                  n(first[i]),
                  n(second[i]),
                  n(d),
                  signLabel(d, strings),
                ]
              : [fmt(i + 1), n(first[i]), n(d), signLabel(d, strings)],
          }))}
        />
      )}
      <Working heading={strings.howCalculated}>
        <p>
          {t(strings.workingCounts, {
            pos: fmt(r.nPositive),
            neg: fmt(r.nNegative),
            zero: fmt(r.nZero),
            n: fmt(r.n),
          })}
        </p>
        <p>{workingP(r, strings, fmt)}</p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNote}
        parts={apa}
      />
    </section>
  );
}
